import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSave, FaArrowLeft } from 'react-icons/fa';
import futbolistaService from '../../services/futbolistaService';
import temporadaService from '../../services/temporadaService';
import ScoutService from '../../services/scoutService';
import TutorLayout from '../../components/tutor/TutorLayout';
import { useGTMContext } from '../../context/GTMContext';
import useWindowSize from '../../hooks/useWindowSize';

// ─── Búsqueda difusa de clubes ────────────────────────────────────────────────

/** Normaliza texto: minúsculas, sin tildes */
const normalizeStr = str =>
  str.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Devuelve el conjunto de bigramas de un string */
const getBigrams = str => {
  const set = new Set();
  for (let i = 0; i < str.length - 1; i++) set.add(str.slice(i, i + 2));
  return set;
};

/** Similitud de Dice sobre bigramas (0–1) */
const bigramSimilarity = (a, b) => {
  const aB = getBigrams(a);
  const bB = getBigrams(b);
  if (aB.size === 0 && bB.size === 0) return 1;
  if (aB.size === 0 || bB.size === 0) return 0;
  let intersection = 0;
  aB.forEach(bg => { if (bB.has(bg)) intersection++; });
  return (2 * intersection) / (aB.size + bB.size);
};

/**
 * Puntúa la coincidencia de un club con el término de búsqueda.
 * Mayor puntuación = mejor resultado.
 */
const scoreClub = (club, rawQuery) => {
  const query = normalizeStr(rawQuery.trim());
  if (!query) return 0;

  const name = normalizeStr(club.nombre);
  const fed  = normalizeStr(club.federacion || '');

  // Nivel 4: empieza exactamente con la query
  if (name.startsWith(query)) return 100;

  // Nivel 3: contiene la query exacta (nombre o federación)
  if (name.includes(query) || fed.includes(query)) return 80;

  // Nivel 2: cada palabra de la query aparece en el nombre (prefijo ≥ 2 chars)
  const queryWords = query.split(/\s+/).filter(w => w.length >= 2);
  if (queryWords.length > 0) {
    const nameWords = name.split(/\s+/);
    const allMatch = queryWords.every(qw =>
      nameWords.some(nw => nw.startsWith(qw) || nw.includes(qw))
    );
    if (allMatch) return 70;

    // Nivel 2b: al menos una palabra de la query (≥3 chars) aparece en el nombre
    const anyLongMatch = queryWords.some(qw => qw.length >= 3 && name.includes(qw));
    if (anyLongMatch) return 50;
  }

  // Nivel 1: similitud de bigramas sobre el nombre completo
  const sim = bigramSimilarity(query, name);
  if (sim >= 0.35) return Math.round(sim * 38); // máx ≈ 38

  return 0;
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Formulario para Crear/Editar Futbolista
 * 
 * Permite a los tutores:
 * - Crear nuevos futbolistas
 * - Editar futbolistas existentes
 * - Gestionar datos básicos y de temporada
 */
const FutbolistaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { isMobile } = useWindowSize();
  const gtm = useGTMContext();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [auxiliarData, setAuxiliarData] = useState(null);
  const [temporadaActiva, setTemporadaActiva] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });

  // Estados para el buscador de clubes
  const [clubSearchTerm, setClubSearchTerm] = useState('');
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const [filteredClubes, setFilteredClubes] = useState([]);
  const [noTengoEquipo, setNoTengoEquipo] = useState(false);
  const [otraComunidad, setOtraComunidad] = useState(false);

  // Estados para captación (scouts)
  const [scoutsActivos, setScoutsActivos] = useState([]);
  const [scoutSearchTerm, setScoutSearchTerm] = useState('');
  const [showScoutDropdown, setShowScoutDropdown] = useState(false);
  const [filteredScouts, setFilteredScouts] = useState([]);

  // Formulario
  const [formData, setFormData] = useState({
    // Datos básicos
    nombre: '',
    fecha_nacimiento: '',
    genero: 'M', // Por defecto masculino
    sede_por_defecto: '',

    // Datos específicos de futbolista
    apodo: '',
    rrss: '',
    foto: '',

    // Datos de temporada
    temporada_id: '', // Ahora es obligatorio seleccionar
    club_id: '',
    division_id: '',
    posicion_1: '',
    posicion_2: '',
    tiene_equipacion: false, // Por defecto no tiene equipación
    // Datos de captación
    origen_captacion: '',
    origen_detalle: '',
    scout_id: ''
  });

  useEffect(() => {
    const loadData = async () => {
      console.log('🔥 INICIANDO CARGA DE DATOS');
      await loadAuxiliarData();
      await loadScoutsActivos();
      if (isEdit && id) {
        console.log('🔥 CARGANDO FUTBOLISTA PARA EDICIÓN');
        await loadFutbolista();
      }
    };
    loadData();
  }, [id, isEdit]);

  // Filtrar clubes cuando cambia el término de búsqueda
  useEffect(() => {
    if (!auxiliarData?.clubes) return;

    // Excluir clubes especiales de la lista de búsqueda normal
    const clubesFiltrados = auxiliarData.clubes.filter(club => club.id !== 64 && club.id !== 265);

    if (clubSearchTerm.trim() === '') {
      // Sin búsqueda: mostrar todos alfabéticamente
      setFilteredClubes([...clubesFiltrados].sort((a, b) => a.nombre.localeCompare(b.nombre)));
    } else {
      // Búsqueda difusa: puntuar cada club y mostrar los que superen 0
      const scored = clubesFiltrados
        .map(club => ({ club, score: scoreClub(club, clubSearchTerm) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score || a.club.nombre.localeCompare(b.club.nombre));

      setFilteredClubes(scored.map(({ club }) => club));
    }
  }, [clubSearchTerm, auxiliarData]);

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showClubDropdown && !event.target.closest('.club-search-group')) {
        setShowClubDropdown(false);
      }
      if (showScoutDropdown && !event.target.closest('.scout-search-group')) {
        setShowScoutDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClubDropdown, showScoutDropdown]);

  // Filtrar scouts cuando cambia el término de búsqueda
  useEffect(() => {
    if (!scoutsActivos.length) return;

    if (scoutSearchTerm.trim() === '') {
      setFilteredScouts(scoutsActivos);
    } else {
      const searchLower = scoutSearchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const filtered = scoutsActivos.filter(scout => {
        const nombreNormalized = scout.nombre.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return nombreNormalized.includes(searchLower);
      });
      setFilteredScouts(filtered);
    }
  }, [scoutSearchTerm, scoutsActivos]);

  // Efecto adicional para debug y forzar re-render
  useEffect(() => {
    console.log('🔥 FORMDATA CAMBIÓ:', formData);
    console.log('🔥 AUXILIAR DATA CAMBIÓ:', auxiliarData);
  }, [formData, auxiliarData]);

  const loadAuxiliarData = async () => {
    try {
      setLoadingData(true);
      const data = await futbolistaService.getAuxiliarData();
      console.log('🔥 DATOS AUXILIARES CARGADOS:', data);
      console.log('🔥 NÚMERO DE CLUBES:', data.clubes?.length);
      console.log('🔥 DATOS AUXILIARES COMPLETOS:', JSON.stringify(data, null, 2));
      setAuxiliarData(data);

      // Cargar temporada activa
      const temporadaResponse = await temporadaService.getTemporadaActiva();
      if (temporadaResponse.success && temporadaResponse.data) {
        setTemporadaActiva(temporadaResponse.data);
        console.log('🔥 TEMPORADA ACTIVA CARGADA:', temporadaResponse.data);
      }
    } catch (error) {
      showAlert('Error al cargar datos auxiliares', 'danger');
      console.error('🔥 ERROR AL CARGAR DATOS:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const loadScoutsActivos = async () => {
    try {
      const scouts = await ScoutService.getScoutsActivos();
      setScoutsActivos(scouts || []);
      setFilteredScouts(scouts || []);
    } catch (error) {
      console.warn('No se pudieron cargar scouts:', error);
      // No es crítico, el formulario funciona sin scouts
    }
  };

  const loadFutbolista = async () => {
    try {
      setLoadingData(true);
      const response = await futbolistaService.getFutbolista(id);
      const futbolista = response.data;

      console.log('=== DATOS DEL FUTBOLISTA CARGADOS ===');
      console.log('Futbolista completo:', futbolista);
      console.log('Temporadas:', futbolista.temporadas);

      // Obtener datos de la temporada actual
      const temporadaActual = futbolista.temporadas?.find(t =>
        temporadaActiva ? t.temporada?.nombre === temporadaActiva.nombre : true
      ) || futbolista.temporadas?.[0];

      console.log('Temporada actual seleccionada:', temporadaActual);

      // Formatear fecha para input date (YYYY-MM-DD)
      let fechaFormateada = '';
      if (futbolista.fecha_nacimiento) {
        const fecha = new Date(futbolista.fecha_nacimiento);
        fechaFormateada = fecha.toISOString().split('T')[0];
      }

      const newFormData = {
        nombre: futbolista.nombre || '',
        fecha_nacimiento: fechaFormateada,
        sede_por_defecto: futbolista.sede_por_defecto || '',
        // Campos específicos de futbolista
        apodo: futbolista.apodo || '',
        rrss: futbolista.rrss || '',
        foto: futbolista.foto || '',
        // Datos de temporada
        temporada_id: temporadaActual?.temporada_id || 1,
        club_id: temporadaActual?.club_id || '',
        division_id: temporadaActual?.division_id || '',
        posicion_1: temporadaActual?.posicion_1 || '',
        posicion_2: temporadaActual?.posicion_2 || '',
        tiene_equipacion: temporadaActual?.tiene_equipacion || false,
        // Datos de captación
        origen_captacion: futbolista.origen_captacion || '',
        origen_detalle: futbolista.origen_detalle || '',
        scout_id: futbolista.scout_id || ''
      };

      // Si tiene scout, establecer el nombre en el buscador
      if (futbolista.scout && futbolista.scout.nombre) {
        setScoutSearchTerm(futbolista.scout.nombre);
      }

      console.log('🔥 DATOS DEL FUTBOLISTA RECIBIDOS:');
      console.log('futbolista.rrss:', futbolista.rrss);
      console.log('futbolista.apodo:', futbolista.apodo);
      console.log('FormData que se va a establecer:', newFormData);
      console.log('🔥 VALOR DE RRSS EN NEWFORMDATA:', newFormData.rrss);

      setFormData(newFormData);
      console.log('FormData después de setFormData - estado actual:', formData);

      // Establecer el nombre del club en el buscador si hay club_id
      if (temporadaActual?.club_id && auxiliarData?.clubes) {
        const club = auxiliarData.clubes.find(c => c.id === temporadaActual.club_id);
        if (club) {
          if (club.id === 64) {
            // Si es "No tengo equipo", marcar el checkbox
            setNoTengoEquipo(true);
            setClubSearchTerm('');
          } else {
            // Si es otro club, establecer el nombre en el buscador
            setNoTengoEquipo(false);
            setClubSearchTerm(club.nombre);
          }
        }
      }

      // Detectar si la división es de "Otra comunidad" para marcar el checkbox
      if (temporadaActual?.division_id && auxiliarData?.divisiones) {
        const division = auxiliarData.divisiones.find(d => d.id === temporadaActual.division_id);
        if (division && division.federacion === 'Otra') {
          setOtraComunidad(true);
        }
      }

    } catch (error) {
      showAlert('Error al cargar futbolista', 'danger');
      console.error('Error al cargar futbolista:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Manejar selección de club desde el dropdown
  const handleClubSelect = (club) => {
    setFormData(prev => ({
      ...prev,
      club_id: club.id
    }));
    setClubSearchTerm(club.nombre);
    setShowClubDropdown(false);
  };

  // Manejar cambio en el input de búsqueda de club
  const handleClubSearchChange = (e) => {
    const value = e.target.value;
    setClubSearchTerm(value);
    setShowClubDropdown(true);

    // Si se borra el texto, limpiar la selección
    if (value.trim() === '') {
      setFormData(prev => ({
        ...prev,
        club_id: ''
      }));
    }
  };

  // Manejar checkbox "No tengo equipo"
  const handleNoTengoEquipoChange = (e) => {
    const checked = e.target.checked;
    setNoTengoEquipo(checked);
    setOtraComunidad(false);

    if (checked) {
      // Buscar dinámicamente el id de "No federado (sin equipo)" en Comunidad Valenciana
      const divNoEquipo = auxiliarData?.divisiones?.find(
        d => d.competicion === 'No federado (sin equipo)' && d.federacion === 'Comunidad Valenciana'
      );
      setFormData(prev => ({
        ...prev,
        club_id: 64,
        division_id: divNoEquipo?.id || 30
      }));
      setClubSearchTerm('');
      setShowClubDropdown(false);
    } else {
      // Si desmarca, limpiar la selección
      setFormData(prev => ({
        ...prev,
        club_id: '',
        division_id: ''
      }));
      setClubSearchTerm('');
    }
  };

  // Manejar checkbox "Juego en otra comunidad autónoma"
  const handleOtraComunidadChange = (e) => {
    const checked = e.target.checked;
    setOtraComunidad(checked);

    // Limpiar division para que elija de las nuevas opciones (o de las normales)
    setFormData(prev => ({
      ...prev,
      division_id: ''
    }));
  };

  // Handlers para captación
  const handleOrigenCaptacionChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      origen_captacion: value,
      // Limpiar campos dependientes al cambiar origen
      origen_detalle: '',
      scout_id: ''
    }));
    // Limpiar buscador de scout si ya no es 'scout'
    if (value !== 'scout') {
      setScoutSearchTerm('');
    }
  };

  const handleScoutSearchChange = (e) => {
    const value = e.target.value;
    setScoutSearchTerm(value);
    setShowScoutDropdown(true);

    // Si se borra el texto, limpiar la selección
    if (value.trim() === '') {
      setFormData(prev => ({
        ...prev,
        scout_id: ''
      }));
    }
  };

  const handleScoutSelect = (scout) => {
    setFormData(prev => ({
      ...prev,
      scout_id: scout.id
    }));
    setScoutSearchTerm(scout.nombre);
    setShowScoutDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Preparar datos finales
      const finalFormData = { ...formData };

      // Validación de origen de captación (obligatorio en creación)
      if (!isEdit && !finalFormData.origen_captacion) {
        showAlert('Debes seleccionar cómo nos conociste', 'warning');
        setLoading(false);
        return;
      }

      // Validación de fecha de nacimiento (años 2010-2019)
      if (finalFormData.fecha_nacimiento) {
        // Verificar que la fecha tenga el formato correcto YYYY-MM-DD
        if (!/^\d{4}-\d{2}-\d{2}$/.test(finalFormData.fecha_nacimiento)) {
          console.error('❌ FORMATO DE FECHA INVÁLIDO:', finalFormData.fecha_nacimiento);
          showAlert(
            '⚠️ La fecha de nacimiento no tiene un formato válido.\n\nPor favor, selecciona una fecha del calendario.',
            'danger'
          );
          setLoading(false);
          return;
        }

        // Extraer el año directamente del string YYYY-MM-DD para evitar problemas de zona horaria
        const anoNacimiento = parseInt(finalFormData.fecha_nacimiento.split('-')[0], 10);
        console.log('🔥 AÑO EXTRAÍDO:', anoNacimiento);

        if (isNaN(anoNacimiento) || anoNacimiento < 2015 || anoNacimiento > 2022) {
          console.error('❌ AÑO FUERA DE RANGO:', anoNacimiento);
          showAlert(
            '⚠️ La fecha de nacimiento debe estar entre los años 2015 y 2022.\n\nPor favor, selecciona una fecha válida.',
            'danger'
          );
          setLoading(false);
          return;
        }

        console.log('✅ VALIDACIÓN DE FECHA PASADA');
      }

      // Validación de duplicados solo en creación
      if (!isEdit) {
        try {
          const futbolistas = await futbolistaService.getFutbolistas();
          const duplicado = futbolistas.find(f =>
            f.nombre?.toLowerCase().trim() === finalFormData.nombre?.toLowerCase().trim() &&
            f.fecha_nacimiento === finalFormData.fecha_nacimiento
          );

          if (duplicado) {
            showAlert(
              `⚠️ Ya existe un futbolista con el nombre "${finalFormData.nombre}" y fecha de nacimiento ${finalFormData.fecha_nacimiento}.\n\nNo se pueden crear futbolistas duplicados.`,
              'danger'
            );
            setLoading(false);
            return;
          }
        } catch (validationError) {
          console.warn('No se pudo validar duplicados en frontend:', validationError);
          // Continuar con la creación, el backend validará
        }
      }

      if (isEdit) {
        const response = await futbolistaService.updateFutbolista(id, finalFormData);
        console.log('🔥 RESPUESTA DEL BACKEND (UPDATE):', response);
        showAlert('Futbolista actualizado exitosamente');
      } else {
        const response = await futbolistaService.createFutbolista(finalFormData);
        console.log('🔥 RESPUESTA DEL BACKEND (CREATE):', response);

        // Enviar evento GTM de creación de futbolista
        gtm.sendCustomEvent('futbolista_created', {
          futbolista_name: finalFormData.nombre,
          futbolista_birth_year: new Date(finalFormData.fecha_nacimiento).getFullYear(),
          club_id: finalFormData.club_id,
          division_id: finalFormData.division_id,
          has_credentials: !!response.data.credenciales,
          tutor_id: response.data?.data?.tutor_id
        });

        // Mostrar credenciales del futbolista si se creó correctamente
        if (response.data.credenciales) {
          const credenciales = response.data.credenciales;
          showAlert(
            `Futbolista creado exitosamente!\n\n` +
            `CREDENCIALES DE ACCESO:\n` +
            `Email: ${credenciales.email}\n` +
            `Contraseña: ${credenciales.contraseña}\n\n` +
            `Comparte estas credenciales con el futbolista para que pueda acceder a la aplicación.`,
            'success'
          );
        } else {
          showAlert('Futbolista creado exitosamente');
        }
      }

      // Redirigir después de un breve delay
      setTimeout(() => {
        navigate('/tutor/futbolistas');
      }, 1500);

    } catch (error) {
      console.log('🔥 ERROR AL ENVIAR:', error);
      console.log('🔥 ERROR RESPONSE:', error.response);

      // Manejar errores específicos
      if (error.response?.status === 409) {
        // Error de conflicto (duplicado)
        showAlert(
          error.response.data.message || 'Ya existe un futbolista con estos datos',
          'danger'
        );
      } else if (error.response?.status === 400) {
        // Error de validación
        showAlert(
          error.response.data.message || 'Datos inválidos. Verifica la información.',
          'danger'
        );
      } else {
        // Error genérico
        showAlert(`Error al ${isEdit ? 'actualizar' : 'crear'} futbolista`, 'danger');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <TutorLayout title="Cargando..." subtitle="Obteniendo datos del futbolista...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ marginBottom: '16px' }}></div>
            <p>Cargando datos...</p>
          </div>
        </div>
      </TutorLayout>
    );
  }

  return (
    <TutorLayout
      title={isEdit ? 'Editar Futbolista' : 'Nuevo Futbolista'}
      subtitle={isEdit ? 'Modifica los datos del futbolista' : 'Registra un nuevo futbolista en el sistema'}
    >
      <button
        className="btn btn-outline-secondary"
        onClick={() => navigate('/tutor/futbolistas')}
        style={{
          marginBottom: isMobile ? '16px' : '20px',
          width: isMobile ? '100%' : 'auto',
          padding: isMobile ? '12px 16px' : '8px 16px',
          fontSize: isMobile ? '15px' : '14px'
        }}
      >
        <FaArrowLeft style={{ marginRight: '8px' }} />
        {isMobile ? 'Volver' : 'Volver a Mis Futbolistas'}
      </button>

      {alert.show && (
        <div className={`alert alert-${alert.variant === 'danger' ? 'danger' : alert.variant === 'success' ? 'success' : 'info'}`} style={{ marginBottom: '20px' }}>
          {alert.message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '20px' : '30px',
          marginBottom: '30px'
        }}>
          {/* Datos básicos */}
          <div className="admin-card">
            <div className="card-header" style={{ margin: '-20px -20px 20px -20px', padding: '20px' }}>
              <h3>Datos Básicos</h3>
            </div>
            <div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Nombre Completo *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: Juan Pérez García"
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Fecha de Nacimiento *</label>
                <input
                  type="date"
                  name="fecha_nacimiento"
                  value={formData.fecha_nacimiento}
                  onChange={handleInputChange}
                  required
                  min="2015-01-01"
                  max="2022-12-31"
                  className="form-input"
                />
                {formData.fecha_nacimiento && (
                  <small className="form-text">
                    Año de nacimiento: {formData.fecha_nacimiento.split('-')[0]}
                  </small>
                )}
                <small className="form-text" style={{ color: '#666', marginTop: '4px' }}>
                  Solo se permiten fechas de nacimiento entre 2015 y 2022
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Género *</label>
                <select
                  name="genero"
                  value={formData.genero}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                >
                  <option value="M">Masculino</option>
                  <option value="F">Femenino</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '0' }}>
                <label className="form-label">Sede por Defecto *</label>
                <select
                  name="sede_por_defecto"
                  value={formData.sede_por_defecto}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                >
                  <option value="">Seleccionar sede...</option>
                  {auxiliarData?.sedes?.map(sede => (
                    <option key={sede.id} value={sede.id}>
                      {sede.nombre} - {sede.poblacion}
                    </option>
                  ))}
                </select>
                <small className="form-text" style={{ display: 'block', marginBottom: '0' }}>
                  Sede preferida para los partidos
                </small>
              </div>
            </div>
          </div>

          {/* Datos de temporada */}
          <div className="admin-card">
            <div className="card-header" style={{ margin: '-20px -20px 20px -20px', padding: '20px' }}>
              <h3>Datos de Temporada</h3>
            </div>
            <div>
              <div className="form-group">
                <label className="form-label">Temporada *</label>
                <select
                  name="temporada_id"
                  value={formData.temporada_id}
                  onChange={handleInputChange}
                  required
                  disabled={isEdit}
                  className="form-input"
                  style={isEdit ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed', opacity: 0.7 } : {}}
                >
                  <option value="">Seleccionar temporada...</option>
                  {auxiliarData?.temporadas?.map(temporada => (
                    <option key={temporada.id} value={temporada.id}>
                      {temporada.nombre}
                    </option>
                  ))}
                </select>
                {isEdit && (
                  <small className="form-text" style={{ color: '#dc3545', marginTop: '4px' }}>
                    La temporada no se puede cambiar una vez creado el futbolista.
                  </small>
                )}
              </div>

              <div className="form-group club-search-group" style={{ position: 'relative' }}>
                <label className="form-label">Club *</label>

                {/* Checkbox "No tengo equipo" */}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'normal'
                  }}>
                    <input
                      type="checkbox"
                      checked={noTengoEquipo}
                      onChange={handleNoTengoEquipoChange}
                      style={{
                        marginRight: '8px',
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ fontWeight: noTengoEquipo ? 'bold' : 'normal' }}>
                      No tengo equipo
                    </span>
                  </label>
                </div>

                {/* Input de búsqueda */}
                <input
                  type="text"
                  value={clubSearchTerm}
                  onChange={handleClubSearchChange}
                  onFocus={() => !noTengoEquipo && setShowClubDropdown(true)}
                  placeholder={noTengoEquipo ? "Has seleccionado 'No tengo equipo'" : "Buscar club..."}
                  required={!noTengoEquipo}
                  disabled={noTengoEquipo}
                  className="form-input"
                  autoComplete="off"
                  style={{
                    backgroundColor: noTengoEquipo ? '#f0f0f0' : 'white',
                    cursor: noTengoEquipo ? 'not-allowed' : 'text',
                    opacity: noTengoEquipo ? 0.6 : 1
                  }}
                />
                <input
                  type="hidden"
                  name="club_id"
                  value={formData.club_id}
                  required
                />

                {/* Dropdown de resultados */}
                {!noTengoEquipo && showClubDropdown && filteredClubes.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}>
                    {/* "Otro equipo" siempre primero */}
                    <div
                      key="otro-equipo"
                      onClick={() => handleClubSelect({ id: 265, nombre: 'Otro equipo' })}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '2px solid #ddd',
                        transition: 'background-color 0.2s',
                        fontSize: '14px',
                        backgroundColor: '#f8f9fa'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    >
                      <div style={{ fontWeight: 'bold', color: '#495057' }}>
                        ⚽ Otro equipo
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                        Mi equipo no aparece en la lista
                      </div>
                    </div>
                    {filteredClubes.slice(0, 50).map(club => (
                      <div
                        key={club.id}
                        onClick={() => handleClubSelect(club)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          transition: 'background-color 0.2s',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <div style={{ fontWeight: 'normal' }}>
                          {club.nombre}
                        </div>
                        {club.federacion && (
                          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                            {club.federacion}
                          </div>
                        )}
                      </div>
                    ))}
                    {filteredClubes.length > 50 && (
                      <div style={{
                        padding: '12px 16px',
                        fontSize: '12px',
                        color: '#6c757d',
                        textAlign: 'center',
                        fontStyle: 'italic'
                      }}>
                        Mostrando 50 de {filteredClubes.length} resultados. Sigue escribiendo para refinar la búsqueda.
                      </div>
                    )}
                  </div>
                )}

                {/* Texto de ayuda */}
                {!noTengoEquipo && (
                  <small className="form-text" style={{ display: 'block', marginTop: '4px' }}>
                    Escribe para buscar tu club. {filteredClubes.length > 0 && `(${filteredClubes.length} resultados)`}
                  </small>
                )}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? '16px' : '20px'
              }}>
                <div className="form-group">
                  <label className="form-label">División *</label>

                  {/* Checkbox "Juego en otra comunidad autónoma" - visible siempre EXCEPTO si "No tengo equipo" está marcado */}
                  {!noTengoEquipo && (
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'normal'
                      }}>
                        <input
                          type="checkbox"
                          checked={otraComunidad}
                          onChange={handleOtraComunidadChange}
                          style={{
                            marginRight: '8px',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{ fontWeight: otraComunidad ? 'bold' : 'normal' }}>
                          Juego en otra comunidad autónoma
                        </span>
                      </label>
                    </div>
                  )}

                  <select
                    name="division_id"
                    value={formData.division_id}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    disabled={noTengoEquipo}
                    style={{
                      backgroundColor: noTengoEquipo ? '#f0f0f0' : 'white',
                      cursor: noTengoEquipo ? 'not-allowed' : 'pointer',
                      opacity: noTengoEquipo ? 0.6 : 1
                    }}
                  >
                    <option value="">Seleccionar división...</option>
                    {(() => {
                      const divisiones = auxiliarData?.divisiones || [];
                      if (otraComunidad && !noTengoEquipo) {
                        /* Si juega en otra comunidad: mostrar divisiones de "Otra comunidad" si existen, si no todas */
                        const otraCom = divisiones.filter(d => d.federacion === 'Otra');
                        if (otraCom.length > 0) {
                          return otraCom.map(division => (
                            <option key={division.id} value={division.id}>
                              {division.competicion}
                            </option>
                          ));
                        }
                        /* Fallback: mostrar todas agrupadas por federación */
                        const federaciones = [...new Set(divisiones.map(d => d.federacion))];
                        return federaciones.map(fed => (
                          <optgroup key={fed} label={fed}>
                            {divisiones.filter(d => d.federacion === fed).map(division => (
                              <option key={division.id} value={division.id}>
                                {division.competicion}
                              </option>
                            ))}
                          </optgroup>
                        ));
                      }
                      /* Normal: agrupar por federación excluyendo "Otra comunidad" */
                      const divsFiltradas = divisiones.filter(d => d.federacion !== 'Otra');
                      const federaciones = [...new Set(divsFiltradas.map(d => d.federacion))];
                      return federaciones.map(fed => (
                        <optgroup key={fed} label={fed}>
                          {divsFiltradas.filter(d => d.federacion === fed).map(division => (
                            <option key={division.id} value={division.id}>
                              {division.competicion}
                            </option>
                          ))}
                        </optgroup>
                      ));
                    })()}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Posición Principal *</label>
                  <select
                    name="posicion_1"
                    value={formData.posicion_1}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  >
                    <option value="">Seleccionar posición...</option>
                    {auxiliarData?.demarcaciones?.filter(demarcacion => demarcacion.id !== 5).map(demarcacion => (
                      <option key={demarcacion.id} value={demarcacion.id}>
                        {demarcacion.posicion}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Posición Secundaria</label>
                <select
                  name="posicion_2"
                  value={formData.posicion_2}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="">Seleccionar posición...</option>
                  {auxiliarData?.demarcaciones?.map(demarcacion => (
                    <option key={demarcacion.id} value={demarcacion.id}>
                      {demarcacion.posicion}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de captación - ¿Cómo nos conociste? (solo en creación) */}
        {!isEdit && (
          <div className="admin-card" style={{ marginBottom: '30px' }}>
            <div className="card-header" style={{ margin: '-20px -20px 20px -20px', padding: '20px' }}>
              <h3>¿Cómo nos conociste?</h3>
            </div>
            <div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label">Origen *</label>
                <select
                  name="origen_captacion"
                  value={formData.origen_captacion}
                  onChange={handleOrigenCaptacionChange}
                  className="form-input"
                  required
                >
                  <option value="">Seleccionar...</option>
                  <option value="scout">Scout</option>
                  <option value="recomendacion_futbolista">Recomendación de otro futbolista</option>
                  <option value="rrss">Redes Sociales</option>
                  <option value="web">Web / Publicidad Online</option>
                  <option value="evento_ap8">Evento AP8 Soccer</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              {/* Scout - Buscador autocomplete */}
              {formData.origen_captacion === 'scout' && (
                <div className="form-group scout-search-group" style={{ marginBottom: '16px', position: 'relative' }}>
                  <label className="form-label">Seleccionar Scout *</label>
                  <input
                    type="text"
                    value={scoutSearchTerm}
                    onChange={handleScoutSearchChange}
                    onFocus={() => setShowScoutDropdown(true)}
                    placeholder="Buscar scout por nombre..."
                    className="form-input"
                    autoComplete="off"
                  />
                  <input type="hidden" name="scout_id" value={formData.scout_id} />

                  {/* Dropdown de scouts */}
                  {showScoutDropdown && filteredScouts.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      zIndex: 1000
                    }}>
                      {filteredScouts.map(scout => (
                        <div
                          key={scout.id}
                          onClick={() => handleScoutSelect(scout)}
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s',
                            fontSize: '14px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          {scout.nombre}
                        </div>
                      ))}
                    </div>
                  )}

                  {showScoutDropdown && filteredScouts.length === 0 && scoutSearchTerm && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      padding: '10px 16px',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      color: '#6c757d',
                      fontStyle: 'italic'
                    }}>
                      No se encontraron scouts con ese nombre
                    </div>
                  )}

                  {formData.scout_id && (
                    <small className="form-text" style={{ color: '#28a745' }}>
                      ✅ Scout seleccionado
                    </small>
                  )}
                </div>
              )}

              {/* RRSS - Dropdown de redes sociales */}
              {formData.origen_captacion === 'rrss' && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label">¿Qué red social? *</label>
                  <select
                    name="origen_detalle"
                    value={formData.origen_detalle}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Seleccionar red social...</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="tiktok">TikTok</option>
                    <option value="twitter">Twitter / X</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>
              )}

              {/* Otro - Texto libre */}
              {formData.origen_captacion === 'otro' && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label">Especificar</label>
                  <input
                    type="text"
                    name="origen_detalle"
                    value={formData.origen_detalle}
                    onChange={handleInputChange}
                    placeholder="¿Cómo nos conociste?"
                    className="form-input"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="admin-card" style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: isMobile ? '12px' : '0'
        }}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate('/tutor/futbolistas')}
              disabled={loading}
              style={{
                width: isMobile ? '100%' : 'auto',
                order: isMobile ? 2 : 1,
                padding: isMobile ? '12px 16px' : '8px 16px',
                fontSize: isMobile ? '15px' : '14px'
              }}
            >
              <FaArrowLeft style={{ marginRight: '8px' }} />
              Cancelar
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{
                width: isMobile ? '100%' : 'auto',
                order: isMobile ? 1 : 2,
                padding: isMobile ? '12px 16px' : '8px 16px',
                fontSize: isMobile ? '15px' : '14px'
              }}
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                  {isEdit ? 'Actualizando...' : 'Creando...'}
                </>
              ) : (
                <>
                  <FaSave style={{ marginRight: '8px' }} />
                  {isMobile ? (isEdit ? 'Actualizar' : 'Crear') : (isEdit ? 'Actualizar Futbolista' : 'Crear Futbolista')}
                </>
              )}
            </button>
        </div>
      </form>
    </TutorLayout>
  );
};

export default FutbolistaForm;
